import { useState, useEffect, useCallback } from 'react';
import { Phone, Calendar, Eye, Clock, AlertTriangle, CheckCircle } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { followUpService } from '../../services/api';
import { Button, Modal, Badge, Skeleton, EmptyState, Toast, Tab } from '../../components/ui';
import { thStyle, tdStyle, iconBtnStyle } from '../../utils/tableStyles';
import LeadDetailView from '../leads/LeadDetailView';
import CallFeedbackForm from '../leads/CallFeedbackForm';

const STATUS_COLORS = {
  NEW_LEAD: '#3B82F6',
  CONTACTED: '#8B5CF6',
  INTERESTED: '#10B981',
  NOT_INTERESTED: '#EF4444',
  ADMISSION_CONFIRMED: '#059669',
};

const TAB_META = {
  today: { icon: Clock, emptyTitle: "No follow-ups for today", emptyDesc: "Leads created or scheduled for follow-up today will appear here." },
  pending: { icon: CheckCircle, emptyTitle: "No pending leads", emptyDesc: "Leads from yesterday with New Lead status will appear here." },
  overdue: { icon: AlertTriangle, emptyTitle: "No overdue leads", emptyDesc: "New leads older than yesterday will appear here." },
  all: { icon: Calendar, emptyTitle: "No follow-ups found", emptyDesc: "Follow-ups will appear here after scheduling callbacks." },
};

const FollowUpManagement = () => {
  const { dark } = useTheme();
  const { user } = useAuth();
  const isRetailLead = user?.role?.code === 'RETAIL_OU_LEAD';
  const isCounsellor = user?.role?.code === 'COUNSELLOR';
  const useLeadView = isRetailLead || isCounsellor;

  const [tab, setTab] = useState('today');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [viewLeadId, setViewLeadId] = useState(null);
  const [callLead, setCallLead] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 20;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (useLeadView) {
        const resp = await followUpService.getLeadsView({ tab, page, limit: PAGE_SIZE });
        setRows(resp.data.data || []);
        setTotal(resp.data.total || 0);
      } else if (tab === 'today') {
        const resp = await followUpService.getToday();
        setRows(resp.data.data || []);
        setTotal((resp.data.data || []).length);
      } else {
        const params = {
          page,
          limit: PAGE_SIZE,
          status: tab === 'pending' ? 'pending' : undefined,
          overdue: tab === 'overdue' ? true : undefined,
        };
        const resp = await followUpService.getAll(params);
        setRows(resp.data.data || []);
        setTotal(resp.data.total || 0);
      }
    } catch {
      setToast({ msg: 'Error loading follow-ups', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [tab, page, useLeadView]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleTabChange = (k) => { setTab(k); setPage(1); };

  // ── Styles ────────────────────────────────────────────────────────────────
  const card = {
    background: dark ? '#0F172A' : '#fff',
    border: `1px solid ${dark ? '#1E293B' : '#E2E8F0'}`,
    borderRadius: 16,
    overflow: 'hidden',
  };
  const totalPages = Math.ceil(total / PAGE_SIZE);

  // ── Tab config ─────────────────────────────────────────────────────────────
  const tabs = useLeadView
    ? [
        { key: 'today', label: "Today's Follow-ups" },
        { key: 'pending', label: 'Pending' },
        { key: 'overdue', label: 'Overdue' },
      ]
    : [
        { key: 'today', label: "Today's Follow-ups" },
        { key: 'pending', label: 'Pending' },
        { key: 'overdue', label: 'Overdue' },
        { key: 'all', label: 'All' },
      ];

  const meta = TAB_META[tab] || TAB_META.all;

  // ── Render lead-based table (RETAIL_OU_LEAD) ───────────────────────────────
  const renderLeadRow = (lead) => (
    <tr key={lead._id} style={{ borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
      <td style={{ ...tdStyle(dark), fontWeight: 600 }}>
        <div>{lead.fullName}</div>
        <div style={{ fontSize: 11, color: dark ? '#64748B' : '#94A3B8' }}>{lead.leadId}</div>
      </td>
      <td style={tdStyle(dark)}>{lead.mobile || '—'}</td>
      <td style={tdStyle(dark)}>{lead.course?.name || '—'}</td>
      <td style={tdStyle(dark)}>
        <Badge color={STATUS_COLORS[lead.statusCode] || '#64748B'}>
          {lead.statusCode?.replace(/_/g, ' ') || 'New Lead'}
        </Badge>
      </td>
      <td style={tdStyle(dark)}>
        {lead.assignedCounsellor
          ? `${lead.assignedCounsellor.firstName} ${lead.assignedCounsellor.lastName}`
          : <span style={{ color: dark ? '#475569' : '#CBD5E1' }}>Unassigned</span>}
      </td>
      <td style={tdStyle(dark)}>
        <div style={{ fontSize: 12 }}>{new Date(lead.createdAt).toLocaleDateString()}</div>
        {lead.nextFollowUpDate && (
          <div style={{ fontSize: 11, color: dark ? '#64748B' : '#94A3B8' }}>
            FU: {new Date(lead.nextFollowUpDate).toLocaleDateString()}
          </div>
        )}
      </td>
      <td style={tdStyle(dark)}>
        <div style={{ display: 'flex', gap: 4 }}>
          <button title="View Lead" style={iconBtnStyle(dark)} onClick={() => setViewLeadId(lead._id)}>
            <Eye size={14} />
          </button>
          <button title="Log Call" style={iconBtnStyle(dark)} onClick={() => setCallLead(lead)}>
            <Phone size={14} />
          </button>
        </div>
      </td>
    </tr>
  );

  // ── Render follow-up-based table (other roles) ─────────────────────────────
  const getFollowUpStatusColor = (fu) => {
    if (fu.status === 'completed') return '#10B981';
    if (new Date(fu.scheduledAt) < new Date()) return '#EF4444';
    return '#3B82F6';
  };

  const renderFollowUpRow = (fu) => (
    <tr key={fu._id} style={{ borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
      <td style={{ ...tdStyle(dark), fontWeight: 600 }}>{fu.lead?.fullName || '—'}</td>
      <td style={tdStyle(dark)}>{fu.lead?.mobile || '—'}</td>
      <td style={tdStyle(dark)}>{fu.lead?.course?.name || '—'}</td>
      <td style={tdStyle(dark)}>
        <div style={{ fontSize: 12 }}>{fu.scheduledAt ? new Date(fu.scheduledAt).toLocaleDateString() : '—'}</div>
        <div style={{ fontSize: 11, color: dark ? '#64748B' : '#94A3B8' }}>
          {fu.scheduledAt ? new Date(fu.scheduledAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
        </div>
      </td>
      <td style={tdStyle(dark)}>
        <Badge color={getFollowUpStatusColor(fu)}>{fu.status || 'pending'}</Badge>
      </td>
      <td style={{ ...tdStyle(dark), maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {fu.remarks || '—'}
      </td>
      <td style={tdStyle(dark)}>
        <div style={{ display: 'flex', gap: 4 }}>
          <button title="View Lead" style={iconBtnStyle(dark)} onClick={() => setViewLeadId(fu.lead?._id)}>
            <Eye size={14} />
          </button>
          {fu.status !== 'completed' && (
            <button title="Log Call" style={iconBtnStyle(dark)} onClick={() => setCallLead(fu.lead)}>
              <Phone size={14} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: dark ? '#F1F5F9' : '#0F172A', margin: 0 }}>Follow-ups</h1>
        <p style={{ fontSize: 13, color: dark ? '#64748B' : '#94A3B8', margin: '4px 0 0' }}>
          {useLeadView
            ? 'Track your leads by follow-up priority'
            : 'Manage your scheduled follow-up calls'}
        </p>
      </div>

      <div style={card}>
        <div style={{ borderBottom: `1px solid ${dark ? '#1E293B' : '#E2E8F0'}` }}>
          <div style={{ padding: '16px 20px 0' }}>
            <Tab tabs={tabs} active={tab} onChange={handleTabChange} />
          </div>
          {useLeadView && tab !== 'today' && (
            <div style={{ padding: '6px 20px 10px' }}>
              <span style={{ fontSize: 12, color: dark ? '#64748B' : '#94A3B8' }}>
                {tab === 'pending'
                  ? isCounsellor
                    ? "Your assigned or created New Leads from yesterday that are still pending."
                    : "New Leads created yesterday in your branch that haven't progressed."
                  : isCounsellor
                    ? "Your assigned or created New Leads older than yesterday that haven't been contacted."
                    : "New Leads older than yesterday in your branch that still haven't been contacted."}
              </span>
            </div>
          )}
        </div>

        <div style={{ overflowX: 'auto' }}>
          {loading ? (
            <div style={{ padding: 20 }}><Skeleton rows={6} cols={isRetailLead ? 7 : 7} /></div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 40 }}>
              <EmptyState icon={meta.icon} title={meta.emptyTitle} description={meta.emptyDesc} />
            </div>
          ) : useLeadView ? (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: dark ? '#0F172A' : '#F8FAFC' }}>
                <tr>
                  <th style={thStyle(dark)}>Lead</th>
                  <th style={thStyle(dark)}>Mobile</th>
                  <th style={thStyle(dark)}>Course</th>
                  <th style={thStyle(dark)}>Status</th>
                  <th style={thStyle(dark)}>Counsellor</th>
                  <th style={thStyle(dark)}>Date</th>
                  <th style={thStyle(dark)}>Actions</th>
                </tr>
              </thead>
              <tbody>{rows.map(renderLeadRow)}</tbody>
            </table>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: dark ? '#0F172A' : '#F8FAFC' }}>
                <tr>
                  <th style={thStyle(dark)}>Lead</th>
                  <th style={thStyle(dark)}>Mobile</th>
                  <th style={thStyle(dark)}>Course</th>
                  <th style={thStyle(dark)}>Scheduled At</th>
                  <th style={thStyle(dark)}>Status</th>
                  <th style={thStyle(dark)}>Remarks</th>
                  <th style={thStyle(dark)}>Actions</th>
                </tr>
              </thead>
              <tbody>{rows.map(renderFollowUpRow)}</tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderTop: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
            <span style={{ fontSize: 12, color: dark ? '#64748B' : '#94A3B8' }}>
              {total} total · Page {page} of {totalPages}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </div>

      <Modal open={!!viewLeadId} onClose={() => setViewLeadId(null)} title="Lead Details" size="xl">
        {viewLeadId && (
          <LeadDetailView
            leadId={viewLeadId}
            onCall={() => {
              const lead = rows.find(r => (r._id === viewLeadId) || (r.lead?._id === viewLeadId));
              if (lead) { setViewLeadId(null); setCallLead(useLeadView ? lead : lead.lead); }
            }}
          />
        )}
      </Modal>

      <Modal open={!!callLead} onClose={() => setCallLead(null)} title="Log Call Feedback" size="md">
        {callLead && <CallFeedbackForm lead={callLead} onClose={() => setCallLead(null)} onUpdated={loadData} />}
      </Modal>
    </div>
  );
};

export default FollowUpManagement;
